/* @flow */
/* eslint jsx-a11y/alt-text: "off" */

import React, { Component } from 'react';
import {
  Grid,
  Icon,
  Button,
  Dimmer,
  Header,
  Message,
  Segment,
  Loader,
} from 'semantic-ui-react';
import Measure from 'react-measure';
import ResourceCard from './ResourceCard';
import ResourceImage from './ResourceImage';
import ResourceDimmer from './ResourceDimmer';
import ImageCrop from './ImageCrop';
import ImageExpand from './ImageExpand';
import * as ResourceApi from 'bucket/api/resources';
import { getImage } from 'utils/resources';
import { blobToURL } from 'utils/blob';

import type { CropConfig } from './ImageCrop';

type Props = {
  resource?: BucketResource,
  config: CropConfig,
  output?: BucketResource,
  raw?: string,
  onSelected: (resource: BucketResource) => void,
  onConfigChanged: (config: CropConfig) => void,
  onImageProcessed?: (url: string) => void,
  onDeleted: () => void,
};

type State = {
  cropOpen: boolean,
  expandOpen: boolean,
  isRendering: boolean,
  renderingError?: Error,
  renderingResult?: string,
  imageDimensions: Object,
};

const styles = {
  result: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '90%',
  },
};

class ServerDrivenResourceCard extends Component {
  props: Props;
  state: State;

  constructor(props: Props) {
    super(props);

    this.state = {
      cropOpen: false,
      expandOpen: false,
      isRendering: false,
      imageDimensions: {},
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props.resource !== nextProps.resource) {
      // Reset rendering when resource changed
      this.setState({
        isRendering: false,
        renderingResult: undefined,
        imageDimensions: {},
      });
    }
  }

  onShowCrop = () => this.setState({ cropOpen: true });
  onHideCrop = () => this.setState({ cropOpen: false });

  onShowExpand = () => this.setState({ expandOpen: true });
  onHideExpand = () => this.setState({ expandOpen: false });

  onImageMeasured = (dimensions: Object) => {
    this.setState({
      imageDimensions: dimensions,
    });
  };

  onResourceSelected = (resource: BucketResource) => {
    this.props.onDeleted();
    this.props.onSelected(resource);
  };

  onResourceDeleted = () => {
    this.props.onDeleted();

    this.setState({
      isRendering: false,
      renderingError: undefined,
      renderingResult: undefined,
    });
  };

  onResourceCrop = (crop: CropConfig) => {
    const { resource } = this.props;
    if (!resource) {
      console.warn('Trying to crop image without source');
      return;
    }

    this.setState(
      {
        cropOpen: false,
      },
      () =>
        this.processAndNotifyConfigChanged(crop, () =>
          ResourceApi.crop(resource.id, crop)
        )
    );
  };

  onResourceExpand = (color: string) => {
    const { resource, config } = this.props;
    if (resource && config && config.aspect) {
      this.setState(
        {
          expandOpen: false,
        },
        () => {
          const crop = {
            aspect: config.aspect,
            color,
          };

          this.processAndNotifyConfigChanged(crop, () =>
            ResourceApi.expand(resource.id, crop)
          );
        }
      );
    } else {
      console.warn('Trying to expand image without source or aspect');
    }
  };

  processAndNotifyConfigChanged(crop: CropConfig, process: Function) {
    this.props.onConfigChanged(crop);

    // Processing async
    this.processResource(process);
  }

  processResource(fetch: Function) {
    const { onImageProcessed } = this.props;

    this.setState({
      isRendering: true,
      renderingResult: undefined,
    });

    // Performing croping server side
    return fetch()
      .then(blobToURL)
      .then(url => {
        this.setState({
          isRendering: false,
          renderingResult: url,
        });

        if (onImageProcessed) {
          onImageProcessed(url);
        }
      })
      .catch(error => {
        this.setState({
          renderingError: error,
        });
      });
  }

  renderProcessingContent() {
    const { resource, output, raw } = this.props;
    const {
      isRendering,
      renderingError,
      renderingResult,
      imageDimensions,
    } = this.state;

    if (renderingError) {
      return (
        <Message
          error
          content="Une erreur est survenue pendant la génération du rendu"
        />
      );
    }

    if (renderingResult || raw) {
      const cropStyle = {
        maxHeight: imageDimensions.height,
      };

      return (
        <div style={styles.result}>
          <div className="ui centered image">
            <img src={renderingResult || raw} style={cropStyle} />
          </div>
        </div>
      );
    }

    if (isRendering) {
      return (
        <Segment style={{ minHeight: '80px' }}>
          <Dimmer active>
            <Loader size="small">Génération du rendu</Loader>
          </Dimmer>
        </Segment>
      );
    }

    if (output) {
      return <ResourceImage resource={output} />;
    }

    const messageContent = resource
      ? 'Veuillez redimensionner la ressource pour générer le rendu'
      : 'Veuillez sélectionnez une ressource';

    return <Message content={messageContent} />;
  }

  renderOperationSelector() {
    const { resource, config } = this.props;
    const { cropOpen, expandOpen } = this.state;

    if (resource) {
      return (
        <div>
          <ImageCrop
            open={cropOpen}
            onClose={this.onHideCrop}
            config={config}
            image={getImage(resource, 'xhdpi_2x')}
            onComplete={this.onResourceCrop}
          />
          <ImageExpand
            open={expandOpen}
            color={config && config.color}
            onClose={this.onHideExpand}
            onUpdate={this.onResourceExpand}
          />
        </div>
      );
    }

    return null;
  }

  // TODO: Expand operation is not available when aspect is not set
  renderDimmer() {
    const { resource, config } = this.props;
    const crop = (!config || !config.color) && (
      <Button
        type="button"
        icon
        labelPosition="right"
        onClick={this.onShowCrop}
      >
        <Icon name="crop" />
        Redimensionner
      </Button>
    );

    const expand = config &&
      config.aspect &&
      !config.width && (
        <Button
          type="button"
          icon
          labelPosition="right"
          onClick={this.onShowExpand}
        >
          <Icon name="maximize" />
          Elargir
        </Button>
      );

    return (
      <ResourceDimmer resource={resource}>
        <Button.Group vertical basic inverted>
          {crop}
          {expand}
        </Button.Group>
      </ResourceDimmer>
    );
  }

  render() {
    const { resource } = this.props;

    return (
      <Grid>
        <Grid.Row columns={2} divided>
          <Grid.Column>
            <Header as="h3" textAlign="center">
              Image originale
            </Header>
            <Measure includeMargin={false} onMeasure={this.onImageMeasured}>
              <ResourceCard
                resourceType="image"
                resource={resource}
                onSelected={this.onResourceSelected}
                onDeleted={this.onResourceDeleted}
              >
                {this.renderDimmer()}
              </ResourceCard>
            </Measure>
            {this.renderOperationSelector()}
          </Grid.Column>
          <Grid.Column textAlign="center">
            <Header as="h3" textAlign="center">
              Rendu
            </Header>
            {this.renderProcessingContent()}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default ServerDrivenResourceCard;



// WEBPACK FOOTER //
// ./src/shared/components/ServerDrivenResourceCard.js